import { Component, Input, OnChanges, OnDestroy } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { Subscription, catchError, finalize, of } from 'rxjs'
import { DotFlashingComponent } from '@nx-superprep/shared'
import { log } from '@nx-superprep/utils'

@Component({
  standalone: true,
  selector: 'gameplay-svgdiagram',
  template: `
    @defer (when isLoaded) { <span [innerHTML]="svgDiagram"></span> }
    @placeholder { <nx-superprep-dot-flashing></nx-superprep-dot-flashing> }
    @if(error) { <div class="error-message">{{ error }}</div> }
  `,
  styleUrls: ['./svg-diagram.component.scss'],
  imports: [DotFlashingComponent],
})
export class SvgDiagramComponent implements OnChanges, OnDestroy {
  @Input() url?: string

  public svgDiagram: SafeHtml = ''
  public isLoaded = false
  public error: string|undefined = undefined

  private subscription?: Subscription

  constructor(
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer,
  ) {}

  public ngOnChanges(): void {
    this.loadSvg()
  }

  private loadSvg(): void {
    if (!this.url) {
      this.svgDiagram = ''
      this.error = ''
      return
    }

    // Cancel previous request if it exists
    this.subscription?.unsubscribe();

    this.isLoaded = false
    this.error = undefined

    this.subscription = this.httpClient
      .get(this.url, { responseType: 'text' })
      .pipe(
        catchError((error: HttpErrorResponse) => { this.handleError(error); return of('')}),
        finalize(() => { this.isLoaded = true })
      )
      .subscribe(value => {
        if (value) {
          // Add accessibility attributes to SVG
          const enhancedSvg = this.enhanceSvgAccessibility(value);
          this.svgDiagram = this.sanitizer.bypassSecurityTrustHtml(enhancedSvg);
        }
      })
  }

  private enhanceSvgAccessibility(svgText: string): string {
    // Simple enhancement to add basic accessibility attributes
    // In a real implementation, this would be more sophisticated
    if (!svgText.includes('role="img"')) {
      svgText = svgText.replace('<svg', '<svg role="img"')
    }

    if (!svgText.includes('aria-label')) {
      svgText = svgText.replace('<svg', '<svg aria-label="Interactive diagram"')
    }

    return svgText
  }

  private handleError(error: HttpErrorResponse): void {
    log.error('SVG loading error:', error)
    this.error = `Failed to load diagram: ${error.status} ${error.statusText}`
    this.svgDiagram = ''
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }
}
